import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../../services/types';
import OpenOnPhone from '../../../components/OpenOnPhone/OpenOnPhone';
import UploadNotice from '../../../components/UploadNotice/UploadNotice';
import { useReceiptPicker } from '../../../hooks/useReceiptPicker';
import {
  uploadErrorTextKey,
  uploadReceipt as sendReceipt,
} from '../../../services/receiptUpload';
import {
  PincoScreen,
  YellowCard,
  PincoSpinnerRing,
  PincoUploadBtn,
  PINCO_GREEN_LIGHT,
} from '../components/PincoLayout';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── SVG icons ────────────────────────────────────────────────────────────────
const RemoveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ cursor: 'pointer', borderRadius: '25%', backgroundColor: 'inherit', transition: '0.5s', flexShrink: 0 }}>
    <path d="M17.6598 17.6598L6.33984 6.33984" stroke="#D63224" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.34016 17.6598L17.6602 6.33984" stroke="#D63224" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SendIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.51 4.23L18.07 8.51C22.91 10.93 22.91 13.07 18.07 15.49L9.51 19.77C2.49 23.28 -0.17 20.62 3.34 13.6L4.21 11.84C4.44 11.39 4.44 10.62 4.21 10.17L3.34 8.4C-0.17 1.38 2.5 -1.28 9.51 4.23Z" stroke="#FF0123" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.44 12H10.84" stroke="#FF0123" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Styled ───────────────────────────────────────────────────────────────────
const WarningIcon = styled.div`
  font-size: 36px;
  text-align: center;
  margin-bottom: 8px;
`;

const WarningTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  color: #ffffff;
  font-family: 'PT Mono', monospace;
  margin-bottom: 6px;
`;

const WarningText = styled.div`
  font-size: 12px;
  color: #8a8a8a;
  text-align: center;
  font-family: 'PT Mono', monospace;
  line-height: 1.5;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  overflow: hidden;
  padding: 8px 24px;
  margin-bottom: 16px;
  background-color: ${PINCO_GREEN_LIGHT};
  border-radius: 24px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.2s ease;
`;

const FileName = styled.p`
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: #FF0123;
  margin: 0;
  word-break: break-all;
`;

const RemoveBtnWrap = styled.div`
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const SendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  appearance: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  color: rgb(46, 125, 50);
  width: 100%;
  text-transform: none;
  font-size: 15px;
  box-shadow: none;
  outline: 0;
  margin: 0 0 16px;
  text-decoration: none;
  padding: 8px 15px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1),
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              color 250ms cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 5px;
  border: 1.5px solid #FF0123;
  &:hover { background-color: rgba(255, 1, 35, 0.08); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SendBtnIconWrap = styled.span`
  display: inherit;
  margin-right: 8px;
  margin-left: -4px;
`;

const SendBtnText = styled.p`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: #FF0123;
  font-weight: 600;
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onConfirm: (file?: any) => Promise<void>;
  onBack: () => void;
  // CPAYMENT-1616: re-upload after a failed auto-verification — show a neutral retry message.
  reupload?: boolean;
}

const PincoUploadPage = ({ transaction, uuid, timeLeft, onConfirm, onBack, reupload = false }: Props) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [errorText, setErrorText] = useState('');
  // CPAYMENT-1786: this screen has no progress bar, so it does not subscribe to progress at all.
  // The failure event still reports `progressPercent` — the service tracks it internally — and
  // subscribing here would only re-render a screen carrying an animated slot machine on every chunk.
  const [retryNotice, setRetryNotice] = useState('');
  // CPAYMENT-1822: the press, the cancel, the empty pick and the silence after it are all handled
  // in one place now — see hooks/useReceiptPicker.
  const picker = useReceiptPicker({
    uuid,
    transaction,
    onFile: setFile,
    onError: (key) => setErrorText(t(key)),
    onBusy: setSending,
    onPickStart: () => {
      setErrorText('');
      setRetryNotice('');
    },
  });

  // CPAYMENT-1786: sending, classification, retry and reporting live in services/receiptUpload —
  // one implementation for every screen, so a failure looks the same wherever it happened.
  const handleSend = async () => {
    if (!file || sending) return;
    setSending(true);
    setErrorText('');
    setRetryNotice('');
    try {
      const result = await sendReceipt(uuid, transaction, file, {
        // This screen only ever replaces a receipt the auto-check rejected, so a 409 here means the
        // backend refused the replacement — not that the file is safely stored.
        isReplacement: true,
        onRetryScheduled: () => setRetryNotice(t('uploadStatus.retrying')),
      });
      setRetryNotice('');

      if (result.ok) {
        await onConfirm();
        return;
      }

        setErrorText(t(uploadErrorTextKey(result.reason, result.httpStatus)));
    } finally {
      setSending(false);
    }
  };

  return (
    <PincoScreen
      step={2}
      uuid={uuid}
      timeLeft={timeLeft}
      actions={[{ label: t('common.back'), onClick: onBack, variant: 'grey' }]}
      // CPAYMENT-1718: под загрузчиком, над «Назад». Во время отправки прячем.
      footerSlot={
        sending ? undefined : (
          <OpenOnPhone
            variant='pinco'
            orLabel={t('common.or')}
            title={t('upload.openOnPhone')}
            hint={t('upload.openOnPhoneHint')}
          />
        )
      }
    >
      <YellowCard>
        <WarningIcon>⚠️</WarningIcon>
        {reupload ? (
          <WarningTitle>{t('verification.reuploadFailed')}</WarningTitle>
        ) : (
          <>
            <WarningTitle>
              {t('upload.required')}<br />
              {t('upload.requiredSub')}
            </WarningTitle>
            <WarningText>{t('upload.instructions')}</WarningText>
          </>
        )}
      </YellowCard>

      {file && (
        <FileRow>
          <FileName>{file.name}</FileName>
          <RemoveBtnWrap onClick={() => setFile(null)}>
            <RemoveIcon />
          </RemoveBtnWrap>
        </FileRow>
      )}

      {errorText && <UploadNotice tone='error'>{errorText}</UploadNotice>}

      {/* CPAYMENT-1786: a retry in flight is neutral — nothing to fix, only to wait. */}
      {retryNotice && !errorText && <UploadNotice tone='neutral'>{retryNotice}</UploadNotice>}

      {file ? (
        <SendBtn onClick={handleSend} disabled={sending}>
          {sending ? (
            <PincoSpinnerRing size={28}>
              <span style={{ fontSize: 8, color: '#FF0123' }}>●</span>
            </PincoSpinnerRing>
          ) : (
            <>
              <SendBtnIconWrap><SendIcon /></SendBtnIconWrap>
              <SendBtnText>{t('upload.sendBtn')}</SendBtnText>
            </>
          )}
        </SendBtn>
      ) : (
        <PincoUploadBtn label={t('upload.uploadBtn')} onClick={picker.open} />
      )}
      {/* CPAYMENT-1822: two presses that produced no file. Whether the dialog failed to open or the
          client left it empty-handed, the page used to say nothing and he just kept tapping. */}
      {picker.helpVisible && <UploadNotice tone='neutral'>{t('upload.pickerHelp')}</UploadNotice>}
    </PincoScreen>
  );
};

export default PincoUploadPage;
